import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import { updateUserDocument } from '../services/database';
import { getUserData } from '../services/auth';
import { Input } from '../components/common/Input';
import { Button } from '../components/common/Button';
import {
  Store,
  User,
  Phone,
  MapPin,
  Building2,
  Mail,
  FileDigit,
  Hash,
  Save,
  CheckCircle2,
  Settings as SettingsIcon
} from 'lucide-react';

const PageContainer = styled.div`
  padding: 24px 20px;
  max-width: 860px;
  margin: 0 auto;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 12px 10px;
  }
`;

const Header = styled.div`
  position: relative;
  padding: 22px 24px;
  margin-bottom: 18px;
  background: linear-gradient(135deg, rgba(71, 85, 105, 0.06) 0%, rgba(14, 116, 144, 0.08) 100%);
  border: 1px solid rgba(14, 116, 144, 0.22);
  border-radius: 20px;
  overflow: hidden;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.8) inset,
    0 6px 20px -8px rgba(14, 116, 144, 0.18);

  &::before {
    content: '';
    position: absolute;
    top: -40%;
    right: -10%;
    width: 220px;
    height: 220px;
    background: radial-gradient(circle, rgba(14, 116, 144, 0.2) 0%, transparent 65%);
    pointer-events: none;
  }

  h1 {
    position: relative;
    margin: 0;
    font-size: 2rem;
    font-weight: 800;
    letter-spacing: -1px;
    background: linear-gradient(135deg, #334155 0%, #0891B2 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  p {
    position: relative;
    margin: 4px 0 0;
    font-size: 13.5px;
    color: #64748b;
    font-weight: 500;
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 16px 14px;
    border-radius: 16px;

    h1 {
      font-size: 1.4rem;
      letter-spacing: -0.5px;
    }
  }
`;

const Panel = styled.div`
  position: relative;
  padding: 0;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(246, 250, 253, 0.95) 100%);
  border: 1px solid rgba(148, 163, 184, 0.16);
  border-radius: 20px;
  overflow: hidden;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.9) inset,
    0 1px 2px rgba(15, 23, 42, 0.04),
    0 6px 20px -8px rgba(46, 91, 136, 0.08);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #334155 0%, #0891B2 50%, #334155 100%);
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    border-radius: 16px;
  }
`;

const PanelBody = styled.div`
  padding: 26px 28px;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 18px 16px;
  }
`;

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 0 14px;
  margin-bottom: 18px;
  border-bottom: 1px dashed rgba(148, 163, 184, 0.3);

  .icon {
    width: 32px;
    height: 32px;
    border-radius: 10px;
    background: linear-gradient(135deg, #334155 0%, #0891B2 100%);
    color: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 10px -2px rgba(8, 145, 178, 0.35);

    svg { width: 16px; height: 16px; stroke-width: 2.3; }
  }

  h3 {
    margin: 0;
    font-size: 15px;
    font-weight: 700;
    color: #1e293b;
    letter-spacing: -0.2px;
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 14px 18px;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: 14px;
  }

  & > .full {
    grid-column: 1 / -1;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 700;
  color: #64748b;
  font-size: 11px;
  letter-spacing: 0.5px;
  text-transform: uppercase;

  svg {
    width: 12px;
    height: 12px;
    color: #0891B2;
    stroke-width: 2.4;
  }

  .required {
    color: #EF4444;
  }
`;

const HelpText = styled.p`
  margin: 0;
  font-size: 12px;
  color: #94a3b8;
  line-height: 1.35;
  padding-left: 2px;

  code {
    font-family: inherit;
    color: #0891B2;
    font-weight: 600;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 22px;
  padding-top: 22px;
  border-top: 1px solid rgba(148, 163, 184, 0.18);
  justify-content: flex-end;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    flex-direction: column-reverse;
  }
`;

const SuccessMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  margin-bottom: 16px;
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.14) 100%);
  border: 1px solid rgba(16, 185, 129, 0.35);
  border-radius: 12px;
  color: #047857;
  font-weight: 600;
  font-size: 14px;
  box-shadow: 0 2px 8px -2px rgba(16, 185, 129, 0.15);
  animation: slideIn 300ms cubic-bezier(0.22, 1, 0.36, 1);

  @keyframes slideIn {
    from { opacity: 0; transform: translateY(-6px); }
    to { opacity: 1; transform: translateY(0); }
  }

  .icon {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: linear-gradient(135deg, #10B981, #059669);
    color: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    svg { width: 14px; height: 14px; stroke-width: 2.8; }
  }
`;

const LoadingState = styled.div`
  padding: 48px 20px;
  text-align: center;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.96) 0%, rgba(246, 250, 253, 0.92) 100%);
  border: 1px dashed rgba(148, 163, 184, 0.35);
  border-radius: 16px;
  color: #94a3b8;
  font-size: 14px;
  font-weight: 500;
`;

export const Settings = () => {
  const { user, refreshUserData } = useAuth();
  const userId = user?.uid;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [businessData, setBusinessData] = useState({
    pharmacyName: '',
    ownerName: '',
    phone: '',
    address: '',
    city: '',
    email: '',
    rfc: 'XAXX010101000'
  });
  const [saleNumber, setSaleNumber] = useState('');

  useEffect(() => {
    if (userId) loadBusinessData();
  }, [userId]);

  const loadBusinessData = async () => {
    try {
      setLoading(true);
      const userData = await getUserData(userId);
      if (userData) {
        setBusinessData({
          pharmacyName: userData.pharmacyName || '',
          ownerName: userData.ownerName || '',
          phone: userData.businessPhone || '',
          address: userData.businessAddress || '',
          city: userData.businessCity || '',
          email: userData.businessEmail || '',
          rfc: userData.rfc || 'XAXX010101000'
        });
        setSaleNumber(userData.lastSaleNumber || '');
      }
    } catch (error) {
      console.error('Error loading business data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setBusinessData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setShowSuccess(false);

      const dataToUpdate = {
        pharmacyName: businessData.pharmacyName,
        ownerName: businessData.ownerName,
        businessPhone: businessData.phone,
        businessAddress: businessData.address,
        businessCity: businessData.city,
        businessEmail: businessData.email,
        rfc: businessData.rfc
      };

      if (saleNumber && saleNumber !== '') {
        dataToUpdate.lastSaleNumber = parseInt(saleNumber);
      }

      await updateUserDocument(userId, dataToUpdate);
      await refreshUserData();

      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 5000);
    } catch (error) {
      console.error('Error saving business data:', error);
      alert('Error al guardar los datos. Por favor, intente nuevamente.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <LoadingState>Cargando configuración...</LoadingState>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Header>
        <h1>Configuración del Negocio</h1>
        <p>Personaliza la información de tu farmacia que aparecerá en facturas y recibos</p>
      </Header>

      {showSuccess && (
        <SuccessMessage>
          <div className="icon"><CheckCircle2 /></div>
          <span>Los cambios se han guardado correctamente</span>
        </SuccessMessage>
      )}

      <Panel>
        <PanelBody>
          <form onSubmit={handleSubmit}>
            <SectionTitle>
              <div className="icon"><Store /></div>
              <h3>Datos del establecimiento</h3>
            </SectionTitle>

            <FormGrid>
              <FormGroup className="full">
                <Label htmlFor="pharmacyName">
                  <Store /> Nombre de la Farmacia <span className="required">*</span>
                </Label>
                <Input
                  id="pharmacyName"
                  name="pharmacyName"
                  type="text"
                  value={businessData.pharmacyName}
                  onChange={handleInputChange}
                  placeholder="Ej: Farmacia San José"
                  required
                />
                <HelpText>Aparecerá en todos los recibos y facturas</HelpText>
              </FormGroup>

              <FormGroup className="full">
                <Label htmlFor="ownerName">
                  <User /> Encargado / Propietario <span className="required">*</span>
                </Label>
                <Input
                  id="ownerName"
                  name="ownerName"
                  type="text"
                  value={businessData.ownerName}
                  onChange={handleInputChange}
                  placeholder="Ej: Juan Pérez"
                  required
                />
                <HelpText>Persona responsable de la farmacia</HelpText>
              </FormGroup>

              <FormGroup>
                <Label htmlFor="phone">
                  <Phone /> Teléfono <span className="required">*</span>
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={businessData.phone}
                  onChange={handleInputChange}
                  placeholder="Ej: 3001234567"
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="email">
                  <Mail /> Correo Electrónico
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={businessData.email}
                  onChange={handleInputChange}
                  placeholder="Ej: info@farmacia.com"
                />
              </FormGroup>

              <FormGroup className="full">
                <Label htmlFor="address">
                  <MapPin /> Dirección <span className="required">*</span>
                </Label>
                <Input
                  id="address"
                  name="address"
                  type="text"
                  value={businessData.address}
                  onChange={handleInputChange}
                  placeholder="Ej: Calle 123 #45-67"
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="city">
                  <Building2 /> Ciudad <span className="required">*</span>
                </Label>
                <Input
                  id="city"
                  name="city"
                  type="text"
                  value={businessData.city}
                  onChange={handleInputChange}
                  placeholder="Ej: Bogotá"
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="rfc">
                  <FileDigit /> NIT / RFC
                </Label>
                <Input
                  id="rfc"
                  name="rfc"
                  type="text"
                  value={businessData.rfc}
                  onChange={handleInputChange}
                  placeholder="Ej: 900123456-7"
                />
              </FormGroup>

              <FormGroup className="full">
                <Label htmlFor="saleNumber">
                  <Hash /> Número Inicial de Venta
                </Label>
                <Input
                  id="saleNumber"
                  name="saleNumber"
                  type="number"
                  value={saleNumber}
                  onChange={(e) => setSaleNumber(e.target.value)}
                  placeholder="Ej: 52000"
                />
                <HelpText>
                  Configure desde qué número empezarán sus ventas. Actual: <code>{saleNumber || '0'}</code>
                </HelpText>
              </FormGroup>
            </FormGrid>

            <ButtonGroup>
              <Button type="submit" disabled={saving}>
                <Save size={16} style={{ marginRight: 6 }} />
                {saving ? 'Guardando...' : 'Guardar Cambios'}
              </Button>
            </ButtonGroup>
          </form>
        </PanelBody>
      </Panel>
    </PageContainer>
  );
};
